import type { ButtonProps } from '@mui/material'
import { Button, ListItemIcon, Menu, MenuItem } from '@mui/material'
import { ExpandLess, ExpandMore } from '@tunasong/icons'
import type { FC, MouseEvent } from 'react'
import { useRef, useState } from 'react'
import { useDimensions } from '../layout/dimensions.js'

interface MenuButtonItem {
  Icon: FC
  name: string
}

export interface MenuButtonSelector<T extends MenuButtonItem> extends Omit<ButtonProps, 'onChange'> {
  items: T[]
  selected?: T
  onChange(item: T): void
}

export const MenuButtonSelector = <T extends MenuButtonItem>(props: MenuButtonSelector<T>) => {
  const { items, selected, onChange, ...restProps } = props

  const { isSmallOrSmaller } = useDimensions()

  const ref = useRef<HTMLButtonElement>(null)

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const handleDropdownClick = (ev: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl => (anchorEl ? null : ev.currentTarget))
  }

  return (
    <>
      <Button
        {...restProps}
        ref={ref}
        sx={{
          textTransform: 'none',
          ...(restProps.sx || {}),
        }}
        disabled={items.length <= 1}
        onClick={handleDropdownClick}
        startIcon={selected?.Icon && <selected.Icon />}
        endIcon={anchorEl ? <ExpandLess /> : <ExpandMore />}
        aria-label="Layout Selector Button"
      >
        {isSmallOrSmaller ? '' : selected?.name}
      </Button>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        {items.map(item => (
          <MenuItem
            key={item.name}
            disabled={item?.name === selected?.name}
            onClick={() => {
              onChange(item)
              setAnchorEl(null)
            }}
          >
            <ListItemIcon>{<item.Icon />}</ListItemIcon>
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default MenuButtonSelector
